import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { useLocation } from "react-router";
import { useNative } from "../../shared/phone/media";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { partnerApi, PartnerApiError } from "../lib/api";
import { AccountNative, PasswordNative } from "./AccountNative";

/**
 * The partner portal profile (node P6): the signed-in partner edits their OWN
 * identity. Every change persists through the real partner-scoped API (the
 * partner is resolved from the session cookie, never a client id) and then
 * refreshes the session so the shell reflects it at once.
 *
 *  - Name + phone save to the partner_auth identity AND every enrollment row that
 *    shares the email, so the program owner sees the same details.
 *  - Email is the login + enrollment join key, so it is read-only here.
 *  - A password change verifies the current password server-side before
 *    re-hashing (same PBKDF2 as signup).
 *  - The photo is a real upload to R2. Honest states, polished light + dark.
 */

function initials(first: string, last: string): string {
  const a = first.trim()[0] ?? "";
  const b = last.trim()[0] ?? "";
  return (a + b || first.trim().slice(0, 2) || "P").toUpperCase();
}

/** Downscale and re-encode a chosen image so avatars stay small and quick. */
async function resizeImage(file: File, max = 512): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    return await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b ?? file), "image/jpeg", 0.85),
    );
  } catch {
    return file;
  }
}

type Note = { ok: boolean; text: string } | null;

export function ProfileView() {
  const { session, applyMe } = usePartnerAuth();
  const partner = session?.partner ?? null;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<Note>(null);

  const [showPw, setShowPw] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<Note>(null);

  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const native = useNative();
  const { pathname } = useLocation();
  const onPasswordScreen = pathname.startsWith("/profile/password");

  // Why: on a phone the password has its own screen; each visit starts empty, with no note left from the last.
  useEffect(() => {
    if (!native) return;
    setCurrentPw("");
    setNewPw("");
    setConfirmPw("");
    setPwMsg(null);
    setProfileMsg(null);
  }, [native, onPasswordScreen]);

  useEffect(() => {
    if (!partner) return;
    setFirstName(partner.firstName);
    setLastName(partner.lastName);
    setPhone(partner.phone ?? "");
  }, [partner]);

  const dirty = useMemo(() => {
    if (!partner) return false;
    return (
      firstName.trim() !== partner.firstName ||
      lastName.trim() !== partner.lastName ||
      phone.trim() !== (partner.phone ?? "")
    );
  }, [partner, firstName, lastName, phone]);

  if (!partner) return null;

  const display = [firstName, lastName].join(" ").trim();

  const onSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setProfileMsg({ ok: false, text: "First and last name are required." });
      return;
    }
    setSaving(true);
    setProfileMsg(null);
    try {
      const me = await partnerApi.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
      });
      applyMe(me);
      setProfileMsg({ ok: true, text: "Saved." });
    } catch (err) {
      setProfileMsg({
        ok: false,
        text: err instanceof PartnerApiError ? err.message : "Could not save. Try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const onChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (!currentPw) {
      setPwMsg({ ok: false, text: "Enter your current password." });
      return;
    }
    if (newPw.length < 8) {
      setPwMsg({ ok: false, text: "New password must be at least 8 characters." });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: "The new passwords do not match." });
      return;
    }
    setPwSaving(true);
    try {
      const me = await partnerApi.updateProfile({
        firstName: firstName.trim() || partner.firstName,
        lastName: lastName.trim() || partner.lastName,
        phone: phone.trim(),
        currentPassword: currentPw,
        newPassword: newPw,
      });
      applyMe(me);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setShowPw(false);
      setPwMsg({ ok: true, text: "Password updated." });
    } catch (err) {
      setPwMsg({
        ok: false,
        text:
          err instanceof PartnerApiError ? err.message : "Could not change your password.",
      });
    } finally {
      setPwSaving(false);
    }
  };

  const onPhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setProfileMsg({ ok: false, text: "Please choose an image file." });
      return;
    }
    setAvatarBusy(true);
    setProfileMsg(null);
    try {
      const blob = await resizeImage(file);
      const me = await partnerApi.uploadAvatar(blob);
      applyMe(me);
    } catch (err) {
      setProfileMsg({
        ok: false,
        text: err instanceof PartnerApiError ? err.message : "Could not upload the photo.",
      });
    } finally {
      setAvatarBusy(false);
    }
  };

  const onRemovePhoto = async () => {
    setAvatarBusy(true);
    setProfileMsg(null);
    try {
      const me = await partnerApi.removeAvatar();
      applyMe(me);
    } catch (err) {
      setProfileMsg({
        ok: false,
        text: err instanceof PartnerApiError ? err.message : "Could not remove the photo.",
      });
    } finally {
      setAvatarBusy(false);
    }
  };

  if (native && onPasswordScreen) {
    return (
      <PasswordNative
        currentPw={currentPw}
        newPw={newPw}
        confirmPw={confirmPw}
        onCurrent={setCurrentPw}
        onNew={setNewPw}
        onConfirm={setConfirmPw}
        busy={pwSaving}
        msg={pwMsg}
        onSubmit={onChangePassword}
      />
    );
  }

  if (native) {
    return (
      <AccountNative
        partner={partner}
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        onFirst={setFirstName}
        onLast={setLastName}
        onPhone={setPhone}
        dirty={dirty}
        saving={saving}
        msg={profileMsg}
        onSave={onSaveProfile}
        onDiscard={() => {
          setFirstName(partner.firstName);
          setLastName(partner.lastName);
          setPhone(partner.phone ?? "");
          setProfileMsg(null);
        }}
        avatarBusy={avatarBusy}
        onPhoto={onPhoto}
        onRemovePhoto={onRemovePhoto}
      />
    );
  }

  return (
    <div className="content app-view prof">
      <header className="prof-head">
        <span className="prof-eyebrow">Account</span>
        <h1>Your profile</h1>
        <p className="prof-lede">Your name, contact details, and how you sign in.</p>
      </header>

      <div className="card prof-card">
        <div className="prof-id">
          <button
            type="button"
            className="prof-av-btn"
            onClick={() => fileRef.current?.click()}
            disabled={avatarBusy}
            title="Change photo"
          >
            {partner.avatarUrl ? (
              <img className="prof-av-img" src={partner.avatarUrl} alt="Your profile photo" />
            ) : (
              <span className="prof-av">
                {initials(firstName || partner.firstName, lastName || partner.lastName)}
              </span>
            )}
            <span className="prof-av-cam" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 8h3l2-2h6l2 2h3v11H4z" />
                <circle cx="12" cy="13" r="3.2" />
              </svg>
            </span>
          </button>
          <div className="prof-who">
            <div className="prof-name">{display || "Partner"}</div>
            <div className="prof-mail">{partner.email}</div>
            <div className="prof-photo-actions">
              <button
                type="button"
                className="prof-link"
                onClick={() => fileRef.current?.click()}
                disabled={avatarBusy}
              >
                {avatarBusy ? "Working..." : partner.avatarUrl ? "Change photo" : "Upload photo"}
              </button>
              {partner.avatarUrl && (
                <button
                  type="button"
                  className="prof-link danger"
                  onClick={onRemovePhoto}
                  disabled={avatarBusy}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="prof-file"
            onChange={onPhoto}
          />
        </div>

        <form className="prof-form" onSubmit={onSaveProfile}>
          <div className="frow">
            <div className="field">
              <label htmlFor="pf-first">First name</label>
              <input
                id="pf-first"
                className="inp"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                autoComplete="given-name"
              />
            </div>
            <div className="field">
              <label htmlFor="pf-last">Last name</label>
              <input
                id="pf-last"
                className="inp"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="pf-email">Email</label>
            <input
              id="pf-email"
              className="inp ro"
              type="email"
              value={partner.email}
              readOnly
              autoComplete="email"
            />
            <p className="prof-hint">
              Your email is how you sign in. To change it, ask the program owner.
            </p>
          </div>

          <div className="field">
            <label htmlFor="pf-phone">Phone</label>
            <input
              id="pf-phone"
              className="inp"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 000 0000"
              autoComplete="tel"
            />
          </div>

          {profileMsg && (
            <div className={`prof-msg ${profileMsg.ok ? "ok" : "err"}`} role="status">
              {profileMsg.text}
            </div>
          )}

          <div className="prof-actions">
            <button type="submit" className="btn" disabled={saving || !dirty}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>

      <div className="card prof-card">
        <div className="prof-sec-head">
          <div>
            <h2 className="prof-sec-title">Password</h2>
            <p className="prof-sec-sub">Change the password you use to sign in.</p>
          </div>
          {!showPw && (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => {
                setShowPw(true);
                setPwMsg(null);
              }}
            >
              Change password
            </button>
          )}
        </div>

        {showPw ? (
          <form className="prof-form" onSubmit={onChangePassword}>
            <div className="field">
              <label htmlFor="pf-cur">Current password</label>
              <input
                id="pf-cur"
                className="inp"
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="frow">
              <div className="field">
                <label htmlFor="pf-new">New password</label>
                <input
                  id="pf-new"
                  className="inp"
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
              </div>
              <div className="field">
                <label htmlFor="pf-conf">Confirm new password</label>
                <input
                  id="pf-conf"
                  className="inp"
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {pwMsg && (
              <div className={`prof-msg ${pwMsg.ok ? "ok" : "err"}`} role="status">
                {pwMsg.text}
              </div>
            )}

            <div className="prof-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setShowPw(false);
                  setCurrentPw("");
                  setNewPw("");
                  setConfirmPw("");
                  setPwMsg(null);
                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn" disabled={pwSaving}>
                {pwSaving ? "Updating..." : "Update password"}
              </button>
            </div>
          </form>
        ) : (
          pwMsg?.ok && (
            <div className="prof-msg ok" role="status">
              {pwMsg.text}
            </div>
          )
        )}
      </div>
    </div>
  );
}
